import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Translation } from 'react-i18next';

import { connect } from 'react-redux';
import { patchCustomTheme, clearPatchCustomTheme } from 'redux/themes/actions';
import { patchUser, clearPatchUser } from 'redux/settings/actions';
import { setMe } from 'redux/users/actions';

import DashboardFormSection
  from 'components/dashboard/common/DashboardFormSection';
import AvatarSelector
  from 'components/dashboard/appearance-content/AvatarSelector';
import CoverImageSelector
  from 'components/dashboard/appearance-content/CoverImageSelector';
import AddUserDescription
  from 'components/dashboard/appearance-content/AddUserDescription';
import ThemesList from 'components/dashboard/appearance-content/ThemesList';
import AppearanceFontStyle
  from 'components/dashboard/appearance-content/AppearanceFontStyle';
import ButtonsStyleSelector
  from 'components/dashboard/appearance-content/ButtonsStyleSelector';
import BackgroundSelector
  from 'components/dashboard/appearance-content/BackgroundSelector';
import SocialIconsColor
  from 'components/dashboard/appearance-content/SocialIconsColor';
import HideLogoCheckbox
  from 'components/dashboard/appearance-content/HideLogoCheckbox';
import HideAbuseReportCheckbox
  from 'components/dashboard/appearance-content/HideAbuseReportCheckbox';
import BaseChangeUsername
 from 'components/dashboard/appearance-content/BaseChangeUsername';
import ChangeShortUsername
  from 'components/dashboard/appearance-content/ChangeShortUsername';
import ReplicateExistingTheme
  from 'components/dashboard/appearance-content/ReplicateExistingTheme';
import CustomAvatarStyleSelector
  from 'components/dashboard/appearance-content/CustomAvatarStyleSelector';
import AvatarQrCodeGenerator
  from 'components/dashboard/appearance-content/AvatarQrCodeGenerator';
import HighLightsContent
  from 'components/dashboard/appearance-content/HighLightsContent';
import PublicPageLayoutSelector
  from 'components/dashboard/appearance-content/PublicPageLayoutSelector';
import PlacementManager
  from 'components/dashboard/appearance-content/PlacementManager';
import CommonMessage from 'components/common/CommonMessage';
import LinksAlignmentSelector from 'components/dashboard/appearance-content/LinksAlignmentSelector';
import CommonPopup from 'components/common/CommonPopup';
import './AppearancePageContent.css';

import { calculateThemesStyle } from './themesStyles';


class AppearancePageContent extends Component {
  static propTypes = {
    patchCustomTheme: PropTypes.func.isRequired,
    clearPatchCustomTheme: PropTypes.func.isRequired,
    setMe: PropTypes.func.isRequired,
    patchUser: PropTypes.func.isRequired,
    clearPatchUser: PropTypes.func.isRequired,
    me: PropTypes.object,
    patchUserSuccess: PropTypes.object,
    patchCustomThemeSuccess: PropTypes.object,
    socialLinksList: PropTypes.arrayOf(PropTypes.shape),
    device: PropTypes.string.isRequired,
  };

  static defaultProps = {
    me: null,
    patchUserSuccess: null,
    patchCustomThemeSuccess: null,
    socialLinksList: null,
  };

  componentDidUpdate(prevProps) {
    if (
      (!prevProps.patchUserSuccess &&
      prevProps.patchUserSuccess !== this.props.patchUserSuccess) ||
      (!prevProps.patchCustomThemeSuccess &&
        prevProps.patchCustomThemeSuccess !== this.props.patchCustomThemeSuccess)
    ) {
      this.props.patchUserSuccess && this.props.setMe(this.props.patchUserSuccess);
      this.props.patchCustomThemeSuccess && this.props.setMe(this.props.patchCustomThemeSuccess);
      this.props.clearPatchUser();
      this.props.clearPatchCustomTheme();
    }
  }

  setCustomMode = () => {
    this.setPredefinedTheme('');
  };

  setPredefinedStyles = themeClassName => {
    const { me } = this.props;
    const customThemeId = me && me.custom_theme && me.custom_theme.id;
    const style = calculateThemesStyle(themeClassName);

    if (style) {
      const form = new FormData();
      Object.keys(style).map(key => key).forEach(key => {
        form.append(key, style[key]);
      });
      form.append('background_style', '');
      this.props.patchCustomTheme(customThemeId, form);
    }
  };

  setPredefinedTheme = themeId => {
    const form = new FormData();
    form.append('theme', themeId);
    this.props.patchUser(form);
  };

  render() {
    const { me, socialLinksList, device } = this.props;

    if (!me) return null;

    const isOptionsAvailable = !me.theme || (me.theme && !me.theme.is_pro);
    const isAvatarOptionAvailable = me && !me.avatar_is_hidden && me.page_layout === 'avatar';

    const socialLinks = socialLinksList &&
      Object.keys(socialLinksList.buttons).length > 0 &&
      Object.keys(socialLinksList.buttons).map(key => ({
        network: key,
        isButton: socialLinksList.buttons[key],
      }));

    return (
      <Translation>
        {(t) => (
          <div className="appearance-page-content__component">
            <div className="double-section">
              {/* USERNAME */}
              <DashboardFormSection
                header={t("USERNAME")}
                expanded={device !== 'desktop'}
                defaultExpandMode={true}
              >
                <BaseChangeUsername />
              </DashboardFormSection>

              {/* SHORT USERNAME */}
              <DashboardFormSection
                proLabel
                header={t("SHORT USERNAME")}
                expanded={device !== 'desktop'}
                defaultExpandMode={true}
              >
                <ChangeShortUsername />
              </DashboardFormSection>
            </div>

            <div className="double-section">
              {/* LAYOUT */}
              <DashboardFormSection
                header={t("LAYOUT")}
                expanded={device !== 'desktop'}
                defaultExpandMode={true}
              >
                <PublicPageLayoutSelector />
              </DashboardFormSection>

              {/* AVATAR or COVER IMAGE */}
              {me.page_layout === 'avatar' ?
                <DashboardFormSection
                  header={t("AVATAR & TITLE")}
                  expanded={device !== 'desktop'}
                  defaultExpandMode={true}
                >
                  <AvatarSelector />
                </DashboardFormSection>
                :
                <DashboardFormSection
                  header={t("COVER IMAGE & TITLE")}
                  expanded={device !== 'desktop'}
                  defaultExpandMode={true}
                >
                  <CoverImageSelector />
                </DashboardFormSection>
              }
            </div>

            {/* QR Code generator */}
            <DashboardFormSection
              header={t("QR CODE")}
              className="dashboard-qr-code-form-section__component"
              expanded
              defaultExpandMode={true}
            >
              <AvatarQrCodeGenerator />
            </DashboardFormSection>

            {/* ADD DESCRIPTION */}
            <DashboardFormSection
              proLabel
              noBordered
              noPadding
              header={t("DESCRIPTION")}
              expanded
              defaultExpandMode={true}
            >
              <AddUserDescription />
            </DashboardFormSection>

            {/* HIGHLIGHTS */}
            <DashboardFormSection
              proLabel
              header={t("HIGHLIGHTS")}
              expanded
              defaultExpandMode={true}
            >
              <HighLightsContent />
            </DashboardFormSection>

            {/* HIGHLIGHTS */}
            <DashboardFormSection
              header={t("PLACEMENT MANAGER")}
              expanded
              defaultExpandMode={true}
            >
              <PlacementManager />
            </DashboardFormSection>

            {/* CUSTOM (PRO) AVATARS */}
            {isAvatarOptionAvailable &&
              <DashboardFormSection
                header={t("CUSTOM AVATARS")}
                expanded
                defaultExpandMode={false}
              >
                <CustomAvatarStyleSelector
                  setCustomMode={this.setCustomMode}
                />
              </DashboardFormSection>
            }

            {/* THEME */}
            <DashboardFormSection
              header={t("THEME")}
              expanded
              defaultExpandMode={true}
            >
              <ThemesList
                setCustomMode={this.setCustomMode}
                setPredefinedStyles={this.setPredefinedStyles}
                setPredefinedTheme={this.setPredefinedTheme}
              />
              {/* <ReplicateExistingTheme /> */}
            </DashboardFormSection>

            {/* Message */}
            {!isOptionsAvailable &&
              <CommonMessage warning>
                {t("PRO themes were already customised; you can't edit them. " +
                  "If you want to add your own colors, buttons, styles " +
                  "etc please select “Custom Theme”.")}
              </CommonMessage>
            }

            {/* BUTTONS */}
            {isOptionsAvailable &&
              <DashboardFormSection
                header={[t("BUTTONS"), 
                <CommonPopup
                  infoIcon
                  type="info"
                >
                  {t("You can change button styles, button icons styles for any normal link. " +
                    "This customisation won’t work for Social Links. " +
                    "This customisation will come later")}
                </CommonPopup>]}
                expanded
                defaultExpandMode={true}
              >
                <ButtonsStyleSelector
                  setCustomMode={this.setCustomMode}
                />   
              </DashboardFormSection>
            }

            {/* BACKGROUND */}
            {isOptionsAvailable &&
              <DashboardFormSection
                header={t("BACKGROUND")}
                expanded
                defaultExpandMode={true}
              >
                <BackgroundSelector
                  setCustomMode={this.setCustomMode}
                />
              </DashboardFormSection>
            }

            {/* FONT STYLE */}
            {isOptionsAvailable &&
              <DashboardFormSection
                header={t("FONT STYLE")}
                expanded
                defaultExpandMode={true}
              >
                <div className="font-style-block">
                  {/*<FontStyleSelector setCustomMode={this.setCustomMode} />*/}
                  <AppearanceFontStyle setCustomMode={this.setCustomMode} />
                </div>
              </DashboardFormSection>
            }

            {/* LINKS TEXT ALIGNMENT */}
            {isOptionsAvailable &&
              <DashboardFormSection
                header={t("LINKS TEXT ALIGNMENT")}
                expanded
                defaultExpandMode={true}
              >
                <LinksAlignmentSelector
                  setCustomMode={this.setCustomMode}
                />
              </DashboardFormSection>
            }

            {/* SOCIAL ICONS STYLE */}
            {isOptionsAvailable &&
              <DashboardFormSection
                header={t("SOCIAL ICONS STYLE")}
                expanded
                defaultExpandMode={true}
              >
                <SocialIconsColor
                  setCustomMode={this.setCustomMode}
                />
              </DashboardFormSection>
            }

            {/* REPLICATE EXISTING THEME */}
            <DashboardFormSection
              proLabel
              header={t("REPLICATE EXISTING HEYLINK.ME THEME")}
              expanded
              defaultExpandMode={false}
              infoUrl="https://blog.heylink.me/2020/11/04/how-to-copy-design-of-the-existing-heylink-me-in-2020/"
            >
              <ReplicateExistingTheme />
            </DashboardFormSection>

            {/* HIDE LOGO */}
            <DashboardFormSection>
              <HideLogoCheckbox />
            </DashboardFormSection>

            {/* HIDE ABUSE REPORT => ONLY FOR PRO */}
            <DashboardFormSection>
              <HideAbuseReportCheckbox />
            </DashboardFormSection>

          </div>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = state => (
  {
    me: state.usersReducer.me,
    patchUserSuccess: state.settingsReducer.patchUserSuccess,
    patchCustomThemeSuccess: state.themesReducer.patchCustomThemeSuccess,
    socialLinksList: state.linksReducer.socialLinksList,
    device: state.commonReducer.device,
  }
);

export default connect(
  mapStateToProps,
  {
    setMe,
    patchUser,
    clearPatchUser,
    patchCustomTheme,
    clearPatchCustomTheme,
  },
)(AppearancePageContent);
